import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Check, 
  X, 
  Star,
  Zap,
  Crown,
  Sparkles,
  CreditCard,
  ArrowRight,
  Infinity as InfinityIcon,
  Users,
  Headphones,
  Code,
  Award,
  Image as ImageIcon,
  Video,
  Cpu,
  Maximize,
  MessageCircle,
  Layers,
  Briefcase,
  Target,
  Server,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';
import { cn } from '../utils';

// Piani di pricing aggiornati
const pricingPlans = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'per sempre',
    credits: 50,
    description: 'Perfetto per iniziare',
    icon: Sparkles,
    gradient: 'from-white/10 to-white/5',
    iconColor: 'text-white/70',
    borderColor: 'border-white/10',
    hoverBorder: 'hover:border-white/20',
    buttonClass: 'pill-ghost',
    popular: false,
    features: {
      images: { value: '10', unit: 'al mese' },
      videos: { value: '2', unit: 'al mese' },
      models: 'Base',
      resolution: 'Standard (1024x1024)',
      support: 'Community',
      api: false,
      batch: false,
      commercial: false,
      priority: false,
      training: false,
      dedicated: false
    }
  },
  {
    id: 'plus',
    name: 'Plus',
    price: 19,
    period: 'al mese',
    credits: 2000,
    description: 'Per creativi seri',
    icon: Zap,
    gradient: 'from-blue-500/20 to-blue-600/5',
    iconColor: 'text-blue-400',
    borderColor: 'border-blue-500/30',
    hoverBorder: 'hover:border-blue-500/50',
    buttonClass: 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-[0_0_20px_-5px_rgba(59,130,246,0.5)] hover:shadow-[0_0_30px_-5px_rgba(59,130,246,0.6)]',
    popular: true,
    features: {
      images: { value: '500', unit: 'al mese' },
      videos: { value: '50', unit: 'al mese' },
      models: 'Tutti i Premium',
      resolution: '4K (4096x4096)',
      support: 'Prioritario',
      api: true,
      batch: true,
      commercial: false,
      priority: true,
      training: false,
      dedicated: false
    }
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 49,
    period: 'al mese',
    credits: 'unlimited',
    description: 'Per professionisti',
    icon: Crown,
    gradient: 'from-purple-500/20 to-purple-600/5',
    iconColor: 'text-purple-400',
    borderColor: 'border-purple-500/30',
    hoverBorder: 'hover:border-purple-500/50',
    buttonClass: 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-[0_0_20px_-5px_rgba(168,85,247,0.5)] hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.6)]',
    popular: false,
    features: {
      images: { value: 'Illimitate', unit: '' },
      videos: { value: 'Illimitati', unit: '4K 60fps' },
      models: 'Esclusivi + Premium',
      resolution: '8K (8192x8192)',
      support: 'Dedicato 24/7',
      api: true,
      batch: true,
      commercial: true,
      priority: true,
      training: true,
      dedicated: true
    }
  }
];

// Features per la tabella comparativa
const comparisonFeatures = [
  {
    category: 'Generazione',
    items: [
      { key: 'images', label: 'Immagini', icon: ImageIcon },
      { key: 'videos', label: 'Video', icon: Video },
      { key: 'models', label: 'Modelli AI', icon: Cpu },
      { key: 'resolution', label: 'Risoluzione Max', icon: Maximize }
    ]
  },
  {
    category: 'Supporto & Servizi',
    items: [
      { key: 'support', label: 'Supporto', icon: MessageCircle },
      { key: 'priority', label: 'Coda Prioritaria', icon: Zap },
      { key: 'api', label: 'Accesso API', icon: Code },
      { key: 'batch', label: 'Batch Processing', icon: Layers }
    ]
  },
  {
    category: 'Avanzate',
    items: [
      { key: 'commercial', label: 'Licenza Commerciale', icon: Briefcase },
      { key: 'training', label: 'Custom Training', icon: Target },
      { key: 'dedicated', label: 'Server Dedicato', icon: Server }
    ]
  }
];

export default function Credits() {
  const [selectedPlan, setSelectedPlan] = useState('plus');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const { user } = useAuth();
  const { credits } = useCredits();

  const handlePurchase = (planId) => {
    console.log('Acquisto piano:', planId);
  };

  const getFeatureValue = (plan, featureKey) => {
    const feature = plan.features[featureKey];
    if (typeof feature === 'object') {
      return `${feature.value} ${feature.unit}`.trim();
    }
    return feature;
  };

  const isFeatureAvailable = (plan, featureKey) => {
    const feature = plan.features[featureKey];
    return feature === true || (typeof feature === 'object' && feature.value !== '0') || (typeof feature === 'string' && feature !== 'No');
  };

  return (
    <div className="min-h-screen pb-24 pt-12">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-[-1]">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-blue-500/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-500/10 rounded-full blur-[120px] animate-pulse delay-1000" />
      </div>

      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <CreditCard size={16} className="text-blue-400" />
              <span className="text-sm font-bold text-white/70 tracking-wider uppercase">Piani e Crediti</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' }}>
              Scegli il piano perfetto
            </h1>
            <p className="text-lg text-white/50 max-w-2xl mx-auto mb-10">
              Passa a un piano premium per sbloccare modelli esclusivi, risoluzioni estreme e generazioni illimitate.
            </p>
            
            {user && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-3 px-6 py-3 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-md"
              >
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Sparkles className="text-blue-400" size={16} />
                </div>
                <span className="text-base font-bold text-white">
                  Hai {credits?.toLocaleString() || '0'} crediti
                </span>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center p-1 bg-white/5 rounded-2xl border border-white/10"
          >
            <button
              onClick={() => setBillingCycle('monthly')}
              className={cn(
                "px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300",
                billingCycle === 'monthly'
                  ? "bg-white/10 text-white shadow-sm"
                  : "text-white/40 hover:text-white/70"
              )}
            >
              Mensile
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={cn(
                "px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 flex items-center gap-2",
                billingCycle === 'yearly'
                  ? "bg-white/10 text-white shadow-sm"
                  : "text-white/40 hover:text-white/70"
              )}
            >
              Annuale
              <span className="bg-blue-500/20 text-blue-400 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider">
                -20%
              </span>
            </button>
          </motion.div>
        </div>

        {/* Plans Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-24">
          {pricingPlans.map((plan, index) => {
            const Icon = plan.icon;
            const price = billingCycle === 'yearly' ? Math.round(plan.price * 0.8) : plan.price;
            const isSelected = selectedPlan === plan.id;
            
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                onClick={() => setSelectedPlan(plan.id)}
                className={cn(
                  "relative panel p-8 transition-all duration-300 cursor-pointer overflow-hidden group rounded-[32px] flex flex-col h-full",
                  plan.borderColor,
                  plan.hoverBorder,
                  isSelected ? "scale-[1.02] border-white/30" : ""
                )}
              >
                {/* Highlight per i piani a pagamento */}
                {plan.popular && (
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500" />
                )}
                {plan.id === 'pro' && (
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
                )}

                {plan.popular && (
                  <div className="absolute top-6 right-6">
                    <div className="bg-blue-500/10 border border-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg shadow-blue-500/10">
                      <Star size={12} fill="currentColor" />
                      Popolare
                    </div>
                  </div>
                )}

                <div className={cn(
                  "w-14 h-14 rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br",
                  plan.gradient
                )}>
                  <Icon size={24} className={plan.iconColor} />
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{plan.name}</h3>
                <p className="text-white/40 text-sm mb-6 h-10">{plan.description}</p>
                
                <div className="mb-8 flex items-end gap-1">
                  <span className="text-5xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' }}>
                    {price === 0 ? 'Gratis' : `€${price}`}
                  </span>
                  {price > 0 && (
                    <span className="text-white/40 mb-1">/{plan.period === 'al mese' ? 'mese' : 'anno'}</span>
                  )}
                </div>
                
                <div className="mt-auto">
                  <div className="mb-6 p-4 rounded-2xl bg-white/5 border border-white/5">
                    <div className="flex items-center gap-3">
                      <CreditCard size={18} className={plan.iconColor} />
                      <span className="font-bold text-white text-sm">
                        {typeof plan.credits === 'number' ? `${plan.credits.toLocaleString()} crediti` : 'Crediti illimitati'}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePurchase(plan.id);
                    }}
                    className={cn(
                      "w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all duration-300",
                      plan.buttonClass
                    )}
                  >
                    {plan.price === 0 ? 'Inizia Gratis' : 'Scegli ' + plan.name}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-24"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Confronta le funzionalità
            </h2>
            <p className="text-white/40">Scopri tutti i dettagli di ogni piano</p>
          </div>
          
          <div className="panel rounded-[32px] overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-4 p-6 border-b border-white/5 bg-white/5">
              <div className="font-bold text-white/50 text-sm uppercase tracking-wider flex items-center">Funzionalità</div>
              {pricingPlans.map((plan) => (
                <div key={plan.id} className="text-center">
                  <div className="font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{plan.name}</div>
                  <div className="text-xs text-white/40">
                    {plan.price === 0 ? 'Gratis' : `€${billingCycle === 'yearly' ? Math.round(plan.price * 0.8) : plan.price}/mese`}
                  </div>
                </div>
              ))}
            </div>

            {/* Table Body */}
            {comparisonFeatures.map((category, categoryIndex) => (
              <div key={category.category}>
                {/* Category Header */}
                <div className="px-6 py-4 bg-white/[0.02] border-b border-white/5">
                  <h3 className="font-bold text-sm text-blue-400 uppercase tracking-widest">{category.category}</h3>
                </div>
                
                {/* Category Items */}
                {category.items.map((item, itemIndex) => {
                  const ItemIcon = item.icon;
                  return (
                    <div 
                      key={item.key} 
                      className="grid grid-cols-4 gap-4 p-6 border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <ItemIcon size={18} className="text-white/30" />
                        <span className="font-semibold text-white/80 text-sm">{item.label}</span>
                      </div>
                      
                      {pricingPlans.map((plan) => (
                        <div key={`${plan.id}-${item.key}`} className="text-center flex items-center justify-center">
                          {isFeatureAvailable(plan, item.key) ? (
                            <div className="flex items-center gap-2">
                              {typeof plan.features[item.key] === 'boolean' ? (
                                <Check size={20} className={plan.iconColor} />
                              ) : (
                                <span className="text-sm font-semibold text-white">
                                  {getFeatureValue(plan, item.key)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <X size={20} className="text-white/20" />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Domande Frequenti
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                question: "Come funzionano i crediti?",
                answer: "Ogni generazione consuma crediti. Le immagini costano 1-5 crediti, i video 10-50 crediti a seconda della qualità e durata.",
                icon: CreditCard
              },
              {
                question: "Posso cambiare piano?",
                answer: "Sì, puoi fare upgrade o downgrade in qualsiasi momento. I crediti non utilizzati vengono mantenuti nel nuovo piano.",
                icon: RefreshCw
              },
              {
                question: "Licenza commerciale?",
                answer: "Il piano Pro include licenza commerciale completa per uso professionale e rivendita delle tue creazioni.",
                icon: Briefcase
              },
              {
                question: "Supporto disponibile?",
                answer: "Community per Free, prioritario per Plus, dedicato 24/7 per Pro con chat e chiamate dirette.",
                icon: Headphones
              }
            ].map((faq, index) => {
              const FaqIcon = faq.icon;
              return (
                <div 
                  key={index}
                  className="panel p-8 rounded-[24px] hover:bg-white/[0.04] transition-colors"
                >
                  <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center mb-6">
                    <FaqIcon size={20} className="text-white/70" />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-3">{faq.question}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}